import { Shape, WallShape } from "../types";
import { raySegmentIntersection } from "./geometry";
import { getAllWallSegments } from "./wallPathSegments";

interface Point {
  x: number;
  y: number;
}

interface WallBridge {
  start: Point;
  end: Point;
}

interface RayResult {
  point: Point;
  distance: number;
}

export type WallBridgeDirection = "horizontal" | "vertical";

const DIRECTION_PAIRS: Record<WallBridgeDirection, [Point, Point]> = {
  horizontal: [{ x: -1, y: 0 }, { x: 1, y: 0 }], // left + right
  vertical: [{ x: 0, y: -1 }, { x: 0, y: 1 }] // up + down
};

const findNearestWallHit = (
  cursor: Point,
  direction: Point,
  walls: WallShape[]
): RayResult | null => {
  let nearest: RayResult | null = null;

  for (const wall of walls) {
    const segment = {
      start: { x: wall.x, y: wall.y },
      end: { x: wall.x2, y: wall.y2 }
    };

    const hit = raySegmentIntersection(cursor, direction, segment);
    if (hit && (nearest === null || hit.distance < nearest.distance)) {
      nearest = { point: hit.point, distance: hit.distance };
    }
  }

  return nearest;
};

/**
 * Find a wall bridge at the cursor position by casting rays in the specified
 * direction pair and connecting the two nearest wall intersections.
 *
 * - "horizontal": casts left + right rays (creates a horizontal bridge)
 * - "vertical": casts up + down rays (creates a vertical bridge)
 */
export const findWallBridge = (
  cursor: Point,
  shapes: Shape[],
  direction: WallBridgeDirection
): WallBridge | null => {
  const walls = getAllWallSegments(shapes);

  if (walls.length === 0) return null;

  const [dirA, dirB] = DIRECTION_PAIRS[direction];
  const hitA = findNearestWallHit(cursor, dirA, walls);
  const hitB = findNearestWallHit(cursor, dirB, walls);

  if (!hitA || !hitB) return null;

  return { start: hitA.point, end: hitB.point };
};
